// src/pages/public/TermsOfService.jsx
import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { ChevronDown, FileText, Shield, AlertCircle, Check } from 'lucide-react'

const TermsOfService = () => {
  const [openSections, setOpenSections] = useState({})

  const toggleSection = (sectionId) => {
    setOpenSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }))
  }

  const sections = [
    {
      id: 'acceptance',
      title: 'Acceptance of Terms',
      content: `By accessing and using YesTimeEarn ("the Platform"), you accept and agree to be bound by the terms and provision of this agreement.`
    },
    {
      id: 'eligibility',
      title: 'Eligibility',
      content: `You must be at least 18 years old to use our services. By using YesTimeEarn, you represent and warrant that you meet all eligibility requirements.`
    },
    {
      id: 'investment',
      title: 'Investment Services',
      content: `YesTimeEarn provides investment opportunities through various packages. All investments carry risk, and past performance does not guarantee future results.`
    },
    {
      id: 'user-responsibilities',
      title: 'User Responsibilities',
      content: `Users are responsible for maintaining the confidentiality of their account credentials and for all activities that occur under their account.`
    },
    {
      id: 'prohibited',
      title: 'Prohibited Activities',
      content: `Users are prohibited from engaging in illegal activities, money laundering, fraud, or any activity that violates applicable laws and regulations.`
    },
    {
      id: 'intellectual-property',
      title: 'Intellectual Property',
      content: `All content on the Platform, including text, graphics, logos, and software, is the property of YesTimeEarn and protected by intellectual property laws.`
    },
    {
      id: 'limitation-liability',
      title: 'Limitation of Liability',
      content: `YesTimeEarn shall not be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of the Platform.`
    },
    {
      id: 'termination',
      title: 'Termination',
      content: `We reserve the right to terminate or suspend access to our Service immediately, without prior notice, for any reason whatsoever.`
    },
    {
      id: 'governing-law',
      title: 'Governing Law',
      content: `These Terms shall be governed by and construed in accordance with the laws of the United Arab Emirates, without regard to its conflict of law provisions.`
    },
    {
      id: 'changes',
      title: 'Changes to Terms',
      content: `We reserve the right to modify or replace these Terms at any time. We will provide notice of any changes by posting the new Terms on this page.`
    }
  ]

  const importantPoints = [
    "All investments carry risk and you should only invest what you can afford to lose",
    "You are responsible for maintaining the security of your account",
    "We comply with international anti-money laundering regulations",
    "Platform fees are clearly disclosed before any transaction",
    "Withdrawal requests are processed according to our published timelines"
  ]

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20 pt-20">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl mx-auto"
          >
            <FileText className="w-16 h-16 mx-auto mb-6" />
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Terms of{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-400">
                Service
              </span>
            </h1>
            <p className="text-xl text-blue-100 mb-8 leading-relaxed">
              Please read these terms and conditions carefully before using our platform
            </p>
            <div className="text-sm text-blue-200">
              Last updated: {new Date().toLocaleDateString()}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Important Notice */}
      <section className="py-12 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex items-start space-x-4 max-w-4xl mx-auto"
          >
            <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400 mt-1 flex-shrink-0" />
            <div>
              <h3 className="text-lg font-bold text-amber-900 dark:text-amber-100 mb-2">
                Important Legal Notice
              </h3>
              <p className="text-amber-800 dark:text-amber-200">
                These Terms of Service constitute a legally binding agreement between you and YesTimeEarn. 
                By using our platform, you acknowledge that you have read, understood, and agree to be bound 
                by these terms.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Terms Content */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Quick Summary */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-lg border border-gray-200 dark:border-gray-700 mb-12"
            >
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6 flex items-center space-x-3">
                <Shield className="w-6 h-6 text-blue-600" />
                <span>Key Points Summary</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {importantPoints.map((point, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                    <span className="text-gray-700 dark:text-gray-300 text-sm">
                      {point}
                    </span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Terms Sections */}
            <div className="space-y-4">
              {sections.map((section, index) => (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden"
                >
                  <button
                    onClick={() => toggleSection(section.id)}
                    className="w-full flex items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                  >
                    <div className="flex items-center space-x-4">
                      <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                        <span className="text-blue-600 dark:text-blue-400 font-bold text-sm">
                          {index + 1}
                        </span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                        {section.title}
                      </h3>
                    </div>
                    <ChevronDown
                      className={`w-5 h-5 text-gray-400 transition-transform ${
                        openSections[section.id] ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  
                  {openSections[section.id] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="px-6 pb-6"
                    >
                      <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
                        {section.content}
                      </p>
                    </motion.div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Contact for Questions */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-8 text-white text-center"
            >
              <h3 className="text-2xl font-bold mb-4">
                Questions About Our Terms?
              </h3>
              <p className="text-blue-100 mb-6">
                If you have any questions about these Terms of Service, please contact our legal team.
              </p>
              <a
                href="/contact"
                className="inline-flex items-center space-x-2 bg-white text-blue-600 hover:bg-gray-100 px-6 py-3 rounded-xl font-semibold transition-all transform hover:scale-105"
              >
                <span>Contact Legal Team</span>
              </a>
            </motion.div>

            {/* Agreement */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-8 text-center"
            >
              <p className="text-sm text-gray-600 dark:text-gray-400">
                By using YesTimeEarn, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default TermsOfService